/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { UserPlus, X } from 'lucide-react';
import { type ChangeEvent, useCallback, useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type Collaborator } from '../../lib/api/collaborators.js';
import { useTherapists } from '../../lib/queries/clinical.js';
import {
  useAddCollaborator,
  useCollaborators,
  useRemoveCollaborator,
} from '../../lib/queries/collaborators.js';

interface ChipProps {
  readonly collaborator: Collaborator;
  readonly onRemove: (therapistId: string) => void;
  readonly disabled: boolean;
}

function CollaboratorChip({ collaborator, onRemove, disabled }: ChipProps): JSX.Element {
  const { t } = useTranslation();
  const remove = useCallback(() => {
    onRemove(collaborator.therapistId);
  }, [onRemove, collaborator.therapistId]);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 py-1 pl-3 pr-1.5 text-sm font-medium text-slate-700">
      {collaborator.fullName}
      <button
        type="button"
        onClick={remove}
        disabled={disabled}
        aria-label={t('therapist.collaborators.remove')}
        className="focus-ring rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-rose-600 disabled:opacity-50"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}

/** Plan collaborators (P1-15c) — chips + an add picker on the plan detail page. */
export function CollaboratorsSection({ planId }: { readonly planId: string }): JSX.Element {
  const { t } = useTranslation();
  const collaborators = useCollaborators(planId);
  const therapists = useTherapists();
  const add = useAddCollaborator(planId);
  const remove = useRemoveCollaborator(planId);

  const list = useMemo(() => collaborators.data ?? [], [collaborators.data]);
  const available = useMemo(() => {
    const taken = new Set(list.map((c) => c.therapistId));
    return (therapists.data ?? []).filter((th) => !taken.has(th.id));
  }, [therapists.data, list]);

  const onAdd = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const therapistId = event.target.value;
      if (therapistId !== '') {
        add.mutate(therapistId);
      }
    },
    [add],
  );
  const onRemove = useCallback(
    (therapistId: string) => {
      remove.mutate(therapistId);
    },
    [remove],
  );
  const busy = add.isPending || remove.isPending;

  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5">
      <p className="mb-3 text-sm font-semibold text-slate-700">
        {t('therapist.collaborators.title')}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {list.length === 0 && (
          <span className="text-sm text-slate-400">{t('therapist.collaborators.none')}</span>
        )}
        {list.map((collaborator) => (
          <CollaboratorChip
            key={collaborator.therapistId}
            collaborator={collaborator}
            onRemove={onRemove}
            disabled={busy}
          />
        ))}
      </div>
      {available.length > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-slate-400" aria-hidden="true" />
          <select
            aria-label={t('therapist.collaborators.add')}
            value=""
            onChange={onAdd}
            disabled={busy}
            className="focus-ring rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          >
            <option value="">{t('therapist.collaborators.add')}</option>
            {available.map((th) => (
              <option key={th.id} value={th.id}>
                {th.fullName}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
